import { ReactElement, useEffect, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';

import dayjs from 'dayjs';

import { ActionProps } from '../../contextProviders/AppProvider';
import { useData } from '../../contextProviders/DataProvider';
import useEventEditorState from '../../stateHooks/useEventEditorState';
import { CoursePair } from '../../types/Course';
import Event from '../../types/Event';
import EventDay from '../../types/EventDay';
import League from '../../types/League';
import Org from '../../types/Org';
import Season from '../../types/Season';
import { ToDayJS } from '../../utils/utils';

import EventAdvancedSettingsEditor from './EventAdvancedSettingsEditor';
import EventDateCalendar from './EventDateCalendar';
import EventEssentialsEditor from './EventEssentialsEditor';
import EventTimeEditor from './EventTimeEditor';
import EventTypeEditor from './EventTypeEditor';

export interface EventEditorProps {
  event: Event;
  league?: League;
  org?: Org;
  seasons?: Season[];
  isNewEvent: boolean;
  /** Limit league choices for a new event to this org (the org the create page was opened from) */
  restrictToOrgId?: string;
  updateEvent?: (event: Partial<Event>) => Promise<void>;
  handleCancel?: () => void;
  handleSubmit?: (event: Event) => void;
  onActionsChange?: (actions: ActionProps[] | null) => void;
  onEventChange?: (event: Event) => void;
}

export default function EventEditor(props: EventEditorProps): ReactElement {
  const { getAdminOrgs } = useData();

  const {
    updatedEvent,
    deleteConfirm,
    isMultiDay,
    isGolf,
    defaultTime,
    handleUpdate,
    handleImageUpdated,
    handleFileSelected,
    handleDelete,
    handleEventDateChange,
    handleEventTimesChange,
    handleLeagueChange,
    handleSeasonChange,
    onCancel,
    onSubmit,
    setDeleteConfirm,
    setIsMultiDay,
    handleIsGolfChange,
  } = useEventEditorState({
    event: props.event,
    league: props.league,
    org: props.org,
    seasons: props.seasons,
    isNewEvent: props.isNewEvent,
    updateEvent: props.updateEvent,
    handleCancel: props.handleCancel,
    handleSubmit: props.handleSubmit,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  // Store callbacks in refs to avoid infinite loops in useEffect
  const onActionsChangeRef = useRef(props.onActionsChange);
  const onEventChangeRef = useRef(props.onEventChange);
  const onSubmitRef = useRef(onSubmit);
  const onCancelRef = useRef(onCancel);
  const handleDeleteRef = useRef(handleDelete);

  useEffect(() => {
    onActionsChangeRef.current = props.onActionsChange;
    onEventChangeRef.current = props.onEventChange;
    onSubmitRef.current = onSubmit;
    onCancelRef.current = onCancel;
    handleDeleteRef.current = handleDelete;
  });

  // Notify parent when org/league changes (so parent can refetch seasons)
  const orgId = updatedEvent.orgId;
  const leagueId = updatedEvent.leagueId;
  useEffect(() => {
    if (onEventChangeRef.current) {
      onEventChangeRef.current(updatedEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, leagueId]);

  // Build page actions for bottom tray (only for new events)
  useEffect(() => {
    if (!onActionsChangeRef.current) return;

    if (!props.isNewEvent) {
      onActionsChangeRef.current(null);
      return;
    }

    const actions: ActionProps[] = [
      { name: 'Review', icon: VisibilityIcon, handleClick: () => onSubmitRef.current(), variant: 'contained', color: 'primary' },
    ];

    onActionsChangeRef.current(actions);
  }, [props.isNewEvent]);

  if (!updatedEvent) return <Container />;

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Stack spacing={2} sx={{pt: 2}}>
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid size={{ xs: 12 }}>
            <Stack spacing={2}>
              <EventEssentialsEditor
                event={updatedEvent}
                isNewEvent={props.isNewEvent}
                restrictToOrgId={props.restrictToOrgId}
                showOrgNames={getAdminOrgs(true).length > 1}
                onUpdate={handleUpdate}
                onLeagueChange={handleLeagueChange}
                onImageUpdate={handleImageUpdated}
                onFileSelected={handleFileSelected}
              />
              <EventTypeEditor
                event={updatedEvent}
                isGolf={isGolf}
                onGolfChange={handleIsGolfChange}
                onUpdate={handleUpdate}
              />
              <Typography variant="body2" sx={{
                color: "text.secondary"
              }}>
                Set the date, location and tee time.
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <EventDateCalendar
              isMultiDay={isMultiDay}
              onChange={handleEventDateChange}
              onMultiDayChange={setIsMultiDay}
              orientation="portrait"
              value={
                // Only pass unique dates to avoid duplicate date issues
                Array.from(
                  new Set(
                    updatedEvent.days.map((day) =>
                      ToDayJS(day.datetime as Timestamp, updatedEvent.timezone).format('YYYY-MM-DD')
                    )
                  )
                ).map((dateStr) => dayjs(dateStr))
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 8 }} sx={{ display: 'flex', alignItems: 'center', '& > *': { width: '100%' } }}>
            <EventTimeEditor
              coursePairs={updatedEvent.coursePairs}
              days={updatedEvent.days}
              defaultTime={defaultTime}
              isGolfEvent={isGolf}
              onChange={(days: EventDay[], coursePairs: CoursePair[]) => handleEventTimesChange(days, coursePairs)}
              timezone={updatedEvent.timezone}
            />
          </Grid>
        </Grid>

        {/* Advanced Settings expander */}
        <Paper variant="outlined" sx={{ overflow: 'hidden', py: 0, my: 0 }}>
          <Button
            fullWidth
            variant="text"
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={showAdvanced ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ justifyContent: 'space-between', px: 2}}
          >
            Advanced Settings
          </Button>
          <Collapse in={showAdvanced}>
            <Box sx={{ px: 1 }}>
              <EventAdvancedSettingsEditor
                event={updatedEvent}
                isGolf={isGolf}
                isTeamLeague={props.league?.isTeam}
                isCatchAllLeague={props.league?.isCatchAll}
                leagueHasDivisions={props.league?.areEventsGroupedByDivision}
                defaultTeamSize={props.league?.defaultTeamSize}
                onUpdate={handleUpdate}
                onSeasonChange={handleSeasonChange}
              />
            </Box>
          </Collapse>
        </Paper>

        {/* Delete action for existing events */}
        {!props.isNewEvent && (
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            {!deleteConfirm ? (
              <Button
                size="small"
                variant="text"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteConfirm(true)}
              >
                Delete event
              </Button>
            ) : (
              <>
                <Button size="small" color="error" onClick={() => handleDeleteRef.current()}>
                  Confirm Delete
                </Button>
                <Button size="small" onClick={() => setDeleteConfirm(false)}>
                  Cancel
                </Button>
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
